import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Navbar } from "@/components/Navbar";
import { useAuth } from "@/contexts/AuthContext";
import { Timer, Users, Volume2, Share2 } from "lucide-react";

const Feature = ({ icon: Icon, label, body }) => (
  <div className="border border-border p-6 transition-colors hover:border-primary/60">
    <div className="mb-4 inline-flex h-9 w-9 items-center justify-center border border-primary/60 text-primary">
      <Icon className="h-4 w-4" />
    </div>
    <div className="font-mono text-xs uppercase tracking-[0.3em] text-muted-foreground mb-2">// {label}</div>
    <div className="text-foreground/90 text-sm leading-relaxed">{body}</div>
  </div>
);

export const Landing = () => {
  const { user } = useAuth();
  return (
    <div className="min-h-screen bg-background bg-grid scanlines">
      <Navbar />
      <main className="mx-auto max-w-7xl px-6 py-16 sm:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7 fade-up">
            <div className="font-mono text-xs uppercase tracking-[0.4em] text-muted-foreground mb-6">
              <span className="text-primary">//</span> faith · innovation · excellence
            </div>
            <h1 className="font-display font-bold text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
              Focus.
              <br />
              <span className="text-primary">Sync.</span>
              <br />
              Ship.
            </h1>
            <p className="mt-8 max-w-xl text-base sm:text-lg text-muted-foreground leading-relaxed">
              A pomodoro timer engineered for Grace the Geek Studios teams. Create focused work blocks,
              share a live timer with anyone, and keep the whole crew on the same clock — no matter where they are.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <Link to={user ? "/dashboard" : "/register"}>
                <Button size="lg" className="rounded-none font-mono uppercase tracking-widest h-12 px-8" data-testid="landing-cta-primary">
                  {user ? "Open Dashboard" : "Initialize Account"}
                </Button>
              </Link>
              <Link to="/login">
                <Button size="lg" variant="outline" className="rounded-none border-border font-mono uppercase tracking-widest h-12 px-8" data-testid="landing-cta-secondary">
                  System Login
                </Button>
              </Link>
            </div>

            <div className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-px bg-border border border-border">
              {[
                ["25:00", "default focus"],
                ["05:00", "short break"],
                ["15:00", "long break"],
                ["∞", "remote sync"],
              ].map(([n, l]) => (
                <div key={l} className="bg-background p-5">
                  <div className="font-mono font-bold text-2xl tracking-tight">{n}</div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground mt-1">{l}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Terminal */}
          <div className="lg:col-span-5 fade-up" style={{ animationDelay: "120ms" }}>
            <div className="border border-border bg-card font-mono text-sm">
              <div className="flex items-center justify-between border-b border-border px-4 py-2">
                <div className="flex gap-1.5">
                  <span className="h-2.5 w-2.5 bg-[#ff5f57]" />
                  <span className="h-2.5 w-2.5 bg-[#febc2e]" />
                  <span className="h-2.5 w-2.5 bg-[#28c840]" />
                </div>
                <div className="text-[11px] uppercase tracking-[0.3em] text-muted-foreground">pomodoro_control.exe</div>
              </div>
              <div className="p-5 space-y-2 text-[13px]">
                <div><span className="text-primary">{">"}</span> gtgs --status</div>
                <div className="text-emerald-400">✓ session: <span className="text-foreground">ACTIVE</span></div>
                <div className="text-emerald-400">✓ team sync: <span className="text-foreground">ONLINE</span></div>
                <div className="text-emerald-400">✓ progress bar: <span className="text-foreground">RENDERED</span></div>
                <div className="text-emerald-400">✓ completion sound: <span className="text-foreground">LOADED</span></div>
                <div className="text-amber-400">⚡ next cycle: <span className="text-foreground">WORK 25:00</span></div>
                <div className="pt-2"><span className="text-primary">{">"}</span> focus · breathe · ship</div>
                <div><span className="text-primary">{">"}</span> <span className="caret" /></div>
              </div>
            </div>
          </div>
        </div>

        {/* Features */}
        <div className="mt-24">
          <div className="section-label mb-4">capabilities</div>
          <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight mb-10">Everything your team needs to ship.</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border">
            <div className="bg-background"><Feature icon={Timer} label="create_timer" body="Classic 25/5/15 pomodoro presets or fully custom durations for focus blocks, short and long breaks." /></div>
            <div className="bg-background"><Feature icon={Users} label="team_share" body="Share a live timer with your team via a 6-letter code. Everyone watches the same countdown, frame-perfect." /></div>
            <div className="bg-background"><Feature icon={Share2} label="remote_sync" body="Server-authoritative time keeps every viewer in lockstep — no drift, no confusion, no excuses." /></div>
            <div className="bg-background"><Feature icon={Volume2} label="completion_sound" body="Pick from bell, chime or digital beep. A clean audio cue when each focus block completes." /></div>
          </div>
        </div>
      </main>
      <footer className="border-t border-border">
        <div className="mx-auto max-w-7xl px-6 py-6 flex flex-wrap items-center justify-between gap-4 font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
          <div>// grace the geek studios · pomodoro 2026</div>
          <div>built for focused teams</div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
